import { Skeleton } from 'antd';
import styled, { css } from 'styled-components';

import { Text, TextProps } from '../../../ui';
import { rem } from '../../../utils';

export type StyledImageCardProps = {
  $source?: string;
  $maxHeight?: number | string;
  $maxWidth?: number | string;
};

export const StyledCardLink = styled.a`
  width: 100%;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${({ theme }) => rem(theme.Spacing.Size.XSmall)};
`;

export const StyledImageWrapper = styled.div<StyledImageCardProps>`
  ${({ theme, $maxHeight = 'auto', $maxWidth = 'auto' }) => css`
    display: flex;
    width: 100%;
    height: auto;
    flex-direction: column;
    align-items: center;
    align-self: stretch;
    border-radius: ${rem(theme.Border.Radius.Medium)};
    overflow: hidden;
    position: relative;

    img {
      max-width: ${typeof $maxWidth === 'string' ? '100%' : rem($maxWidth)};
      width: auto;
      height: ${typeof $maxHeight === 'string' ? 'auto' : rem($maxHeight)};
      object-fit: contain;
      border-radius: ${rem(theme.Border.Radius.Medium)};
    }
  `}
`;

export const StyledImageCardText = styled(
  (props: { children: React.ReactNode; size: TextProps['size'] }) => <Text {...props} />
)`
  text-align: center;
  word-wrap: break-word;
`;

export const StyledImageCardSkeletonText = styled(Skeleton.Button)<StyledImageCardProps>`
  &&& {
    height: 16px;
  }
`;

export type StyledCardProps = {
  width?: number;
  height?: number;
  selected?: boolean;
  disabled?: boolean;
};

export const StyledWrapper = styled.div<StyledCardProps>`
  ${({ theme, width, height, selected, disabled }) => css`
    background-color: ${theme.Components.Panel.Background};
    display: flex;
    ${width && `width: ${rem(width)}`};
    ${height && `width: ${rem(height)}`};
    padding: ${rem(theme.Spacing.Size.XSmall)};
    border-radius: ${rem(theme.Border.Radius.Large)};
    transition: box-shadow ${theme.Transition.Timing.Out};
    box-shadow: inset 0 0 0 ${rem(selected ? theme.Border.Size.Medium : theme.Border.Size.Small)}
      ${selected ? theme.Color.Brand.Primary : theme.Color.Neutrals.Border};
    cursor: ${disabled ? 'initial' : 'pointer'};
    &:hover {
      box-shadow: inset 0 0 0 ${rem(theme.Border.Size.Medium)}
        ${disabled ? theme.Color.Neutrals.Border : theme.Color.Brand.Primary};
      transition: box-shadow ${theme.Transition.Timing.In};
    }

    & .ant-image-mask {
      border-radius: ${rem(theme.Border.Radius.Medium)};
    }
  `}
`;

export const StyledBadge = styled.span<{
  direction: 'bottom-left' | 'bottom-right';
}>`
  position: absolute;
  ${({ theme, direction }) =>
    direction === 'bottom-left' &&
    css`
      left: ${rem(theme.Sizes.XSmall)};
      bottom: ${rem(theme.Sizes.XSmall)};
    `}

  ${({ theme, direction }) =>
    direction === 'bottom-right' &&
    css`
      right: ${rem(theme.Sizes.XSmall)};
      bottom: ${rem(theme.Sizes.XSmall)};
    `}

  background-color: ${({ theme }) => theme.Color.OverlayFixed.Default};
  padding: ${({ theme }) => rem(theme.Spacing.Size.XXSmall)};
  border-radius: ${({ theme }) => rem(theme.Border.Radius.XSmall)};
`;
